import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BsPersonWorkspace } from 'react-icons/bs';
import { GrFormNext, GrFormPrevious } from 'react-icons/gr';
import { IoIosArrowDown } from 'react-icons/io'; // react-icons/io 임포트
import { Link, useLocation } from 'react-router-dom';

import { createTooltip, removeTooltip } from '@/widgets/LayoutSidebar/ui/SideBarTooltip'; // 툴팁 유틸 함수 임포트

import { bottomSidebarItems, sidebarItems } from '../model/items';
import { MenuItemType, SidebarItem } from '../model/types';

import styles from './LayoutSidebar.module.scss';

import { CompanyManagementModal } from '@/features/Modals/CompanyManagementModal/CompanyManagementModal';
import { routes } from '../../../app/constants';
import { CompanyContext } from '../../../app/providers/ParamsProvider/ParamsProvider';

// localStorage 키 상수
const SIDEBAR_MENU_OPEN_KEY = 'sidebar-menu-open';

// localStorage에서 사이드바 상태를 불러오는 함수
const getSidebarStateFromStorage = (): boolean => {
    try {
        const stored = localStorage.getItem(SIDEBAR_MENU_OPEN_KEY);
        return stored !== null ? JSON.parse(stored) : true; // 기본값은 true (펼쳐진 상태)
    } catch (error) {
        console.warn('사이드바 상태를 불러오는 중 오류:', error);
        return true; // 오류 시 기본값 반환
    }
};

// localStorage에 사이드바 상태를 저장하는 함수
const saveSidebarStateToStorage = (isOpen: boolean): void => {
    try {
        localStorage.setItem(SIDEBAR_MENU_OPEN_KEY, JSON.stringify(isOpen));
    } catch (error) {
        console.warn('사이드바 상태를 저장하는 중 오류:', error);
    }
};

interface SideBarProps {
    children?: React.ReactNode;
}

export const LayoutSidebar: React.FC<SideBarProps> = ({ children }) => {

    const companyInfo = useContext(CompanyContext);

    // localStorage에서 초기 상태를 불러와서 설정
    const [menuOpen, setMenuOpen] = useState(getSidebarStateFromStorage);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);

    const sidebarRef = useRef<HTMLDivElement>(null);

    const location = useLocation();

    // 새 페이지 이동 시 tab=0으로 설정하는 함수
    const getSearchWithTab = () => {
        const urlParams = new URLSearchParams(location.search);
        urlParams.set('tab', '0');
        return urlParams.toString();
    };

    // 경로 -> 카테고리 매핑 객체를 한 번만 생성 (성능 최적화)
    const pathToCategoryMap = useMemo(() => {
        const map: Record<string, string> = {};

        const categoryMapping = {
            'Accounting': '회계관리',
            'Vat': '부가가치세',
            'SalaryInsurance': '급여보험',
            'Coperate': '법인조정',
            'Personal': '개인조정',
            'Common': '자료수집관리',
        };

        for (const [categoryKey, categoryData] of Object.entries(routes)) {
            const categoryTitle = categoryMapping[categoryKey as keyof typeof categoryMapping];
            if (categoryTitle) {
                for (const pageData of Object.values(categoryData as Record<string, any>)) {
                    map[pageData.path] = categoryTitle;
                }
            }
        }

        return map;
    }, []); // routes는 변경되지 않으므로 빈 의존성 배열

    // O(1) 조회로 성능 최적화
    const getActiveMenuTitle = (pathname: string): string | null => {
        return pathToCategoryMap[pathname] || null;
    };

    const activeMenuTitle = getActiveMenuTitle(location.pathname);

    // 메뉴 항목이 활성화되어 있는지 확인하는 함수
    const isMenuActive = (itemTitle: string): boolean => {
        return activeMenuTitle === itemTitle;
    };

    const onMouseEnter = (event: React.MouseEvent<HTMLSpanElement>) => {
        if (!menuOpen) { // menuOpen이 false일 때만 툴팁 생성

            const target = event.currentTarget as HTMLElement;
            if (!expandedItem) {
                createTooltip(target); // 툴팁 생성 함수 호출
            }
        }
    };

    const onMouseLeave = () => {
        removeTooltip(); // 툴팁 제거 함수 호출
    };

    // menuOpen 상태가 변경될 때 툴팁 제거
    useEffect(() => {
        if (menuOpen) {
            removeTooltip();
        }
    }, [menuOpen]);

    const handleMenuClick = (item: SidebarItem) => {
        if (item.menuType === MenuItemType.CATEGORY) {
            if (expandedItem !== item.title) {
                setExpandedItem(item.title);
                if (!menuOpen) {
                    setMenuOpen(true); // 메뉴가 닫혀있을 때 자동으로 열기
                    saveSidebarStateToStorage(true); // localStorage에 상태 저장
                }
                removeTooltip();
            } else {
                setExpandedItem(null);
            }
        } else if (item.menuType === MenuItemType.BUTTON && item.to) {
            // Link 컴포넌트 사용으로 변경 (onClick 제거)
        }
    };

    const handleToggleMenu = () => {
        const newMenuOpen = !menuOpen;
        setMenuOpen(newMenuOpen);
        saveSidebarStateToStorage(newMenuOpen); // localStorage에 상태 저장
        if (menuOpen) {
            setExpandedItem(null);
        }
    };

    return (
        <nav ref={sidebarRef} className={menuOpen ? styles.sidebar + ' ' + styles.open : styles.sidebar}>
            <div className={styles.toggleWrap}>
                <div className={styles.logoContainer}>
                    {menuOpen && (
                        <span className={styles.topArea}>
                            <a
                                href={`https://www.xadom.ai/${companyInfo?.workspace?.workspaceName || ''}`}
                                className={styles.topButton}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <BsPersonWorkspace />
                                <span className={styles.workspaceName}>
                                    {companyInfo?.workspace?.workspaceName || 'Workspace'}
                                </span>
                            </a>
                        </span>
                    )}
                    <span
                        onClick={handleToggleMenu}
                        className={`${styles.toggleBtn} ${menuOpen ? styles.open : ''}`}>
                        {menuOpen ? <GrFormPrevious /> : <GrFormNext />}
                    </span>
                </div>
            </div>
            <div className={styles.sidebarContent}>
                <ul>
                    {sidebarItems().map((item: SidebarItem) => (
                        <li key={item.title}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            data-title={item.title}
                            className={expandedItem === item.title ? styles.expanded : ''}
                        >
                            {item.menuType === 'button' ? (
                                item.onClick ? (
                                    <button
                                        className={`${styles.sidebarBtn} ${isMenuActive(item.title) ? styles.active : ''}`}
                                        onClick={item.onClick}
                                    >
                                        <span className={styles.sidebarIcon}>
                                            {item.icon}
                                        </span>
                                        {menuOpen &&
                                            <div className={styles.sidebarTitle}>
                                                {item.title}
                                            </div>
                                        }
                                    </button>
                                ) : (
                                    <Link
                                        to={{
                                            pathname: item.to,
                                            search: getSearchWithTab()
                                        }}
                                        className={`${styles.sidebarBtn} ${isMenuActive(item.title) ? styles.active : ''}`}
                                    >
                                        <span className={styles.sidebarIcon}>
                                            {item.icon}
                                        </span>
                                        {menuOpen &&
                                            <div className={styles.sidebarTitle}>
                                                {item.title}
                                            </div>
                                        }
                                    </Link>
                                )
                            ) : (
                                <button className={styles.sidebarBtn} onClick={() => handleMenuClick(item)}>
                                    <span className={styles.sidebarIcon}>
                                        {item.icon}
                                    </span>
                                    {menuOpen &&
                                        <div className={styles.sidebarTitle}>
                                            {item.title}
                                        </div>
                                    }
                                    {item.menuType === 'category' && menuOpen && (
                                        <span className={styles.categoryArrow}>
                                            <IoIosArrowDown />
                                        </span>
                                    )}
                                </button>
                            )}
                            {item.menuType === 'category' && expandedItem === item.title && item.options && (
                                <ul className={styles.subMenu}>
                                    {item.options.map((option: SidebarItem) => (
                                        <li key={option.title} className={styles.subMenuItem}>
                                            {option.menuType === 'button' ? (
                                                <Link
                                                    to={{
                                                        pathname: option.to,
                                                        search: getSearchWithTab()
                                                    }}
                                                    className={styles.subMenuLink}
                                                    onClick={() => setExpandedItem(null)}
                                                >
                                                    {option.title}
                                                </Link>
                                            ) : option.menuType === 'category' ? (
                                                <button className={styles.subMenuLink} onClick={() => handleMenuClick(option)}>
                                                    {option.title}
                                                </button>
                                            ) : null}
                                            {option.menuType === 'category' && expandedItem === option.title && option.options && (
                                                <ul className={styles.subMenu}>
                                                    {option.options.map((nestedOption: SidebarItem) => (
                                                        <li key={nestedOption.title} className={styles.subMenuItem}>
                                                            <Link
                                                                to={{
                                                                    pathname: nestedOption.to,
                                                                    search: getSearchWithTab()
                                                                }}
                                                                className={styles.subMenuLink}
                                                                onClick={() => setExpandedItem(null)}
                                                            >
                                                                {nestedOption.title}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
            <div className={styles.bottomMenu}>
                <ul>
                    {bottomSidebarItems().map((item: SidebarItem, index) => (
                        item.menuType === MenuItemType.DIVIDER ? (
                            <li key={`divider-${index}`} className={styles.divider} />
                        ) : (
                            <li key={item.title}
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                data-title={item.title}
                            >
                                {item.onClick ? (
                                    <button className={styles.sidebarBtn} onClick={item.onClick}>
                                        <span className={styles.sidebarIcon}>
                                            {item.icon}
                                        </span>
                                        {menuOpen &&
                                            <div className={styles.sidebarTitle}>
                                                {item.title}
                                            </div>
                                        }
                                    </button>
                                ) : (
                                    <Link
                                        to={{
                                            pathname: item.to,
                                            search: getSearchWithTab()
                                        }}
                                        className={styles.sidebarBtn}
                                    >
                                        <span className={styles.sidebarIcon}>
                                            {item.icon}
                                        </span>
                                        {menuOpen &&
                                            <div className={styles.sidebarTitle}>
                                                {item.title}
                                            </div>
                                        }
                                    </Link>
                                )}
                            </li>
                        )
                    ))}
                </ul>
            </div>
            <CompanyManagementModal />
            {children}
        </nav>
    );
};